import { padBitmapTo, rotateBitmap90, type MonoBitmap } from "./bitmap";
import { layoutNote, type LaidOutNote, type NoteOrientation } from "./noteLayout";
import type { NoteFont } from "./noteFonts";
import { toMonoBitmap } from "./receiptRender";

/**
 * G-65 — a typed message into the dots that burn.
 *
 * ONE PATH, used by the preview and the print alike, exactly as the receipt
 * does. The florist approves a picture of the sticker; the bytes are made from
 * that same picture, so the two cannot drift.
 *
 * Synchronous on purpose. The preview redraws on every keystroke, and an async
 * render would either flicker or need coalescing that guesses when it is safe
 * to draw. Fonts that must be downloaded are the caller's problem to settle
 * BEFORE calling (see `fontsReady` on the panel) — measuring text in a font the
 * browser has not loaded silently measures the fallback, which would look right
 * on screen and print in the wrong face.
 */

/**
 * Which way a vertical note is turned.
 *
 * Decides whether the strip reads bottom-to-top or top-to-bottom as it leaves
 * the printer, which depends on which end a florist treats as the top when
 * they stick it to a card. That cannot be settled from a desk — it is settled
 * on the first real sticker, and this constant is the one character to change.
 */
export const VERTICAL_TURN: "cw" | "ccw" = "cw";

export interface RenderedNote {
  /** Exactly what goes to the printer: paper-width, rotated, padded. */
  bitmap: MonoBitmap;
  layout: LaidOutNote;
  /** Paper used, in millimetres, for a florist comparing it to a card. */
  lengthMm: number;
}

/** 203 dpi — 8 dots per millimetre, the whole cat-printer family. */
const DOTS_PER_MM = 8;

export function renderNote(input: {
  text: string;
  font: NoteFont;
  orientation: NoteOrientation;
  widthDots: number;
  /**
   * The florist's size choice, a CEILING. Omitted means auto-fit's own default.
   * The face's floor still wins underneath it, so a face that cannot print
   * small is never asked to.
   */
  maxPx?: number;
}): RenderedNote {
  const { text, font, orientation, widthDots, maxPx } = input;

  const probe = document.createElement("canvas").getContext("2d")!;
  const measure = (value: string, px: number) => {
    probe.font = `${font.weight} ${px}px ${font.stack}`;
    return probe.measureText(value).width;
  };

  const layout = layoutNote(text, orientation, widthDots, measure, {
    minPx: font.minPx,
    maxPx,
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(layout.width));
  canvas.height = Math.max(1, Math.ceil(layout.height));
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "center";
  for (const op of layout.ops) {
    ctx.font = `${font.weight} ${op.px}px ${font.stack}`;
    ctx.fillText(op.text, op.x, op.y);
  }

  let bitmap = toMonoBitmap(canvas);
  if (orientation === "vertical") {
    // The quarter turn is what makes the bounded axis the LINE COUNT rather
    // than the line length — see the geometry note in `bitmap.ts`.
    bitmap = rotateBitmap90(bitmap, VERTICAL_TURN);
  }
  // Centred on the roll, so the strip peels straight instead of hugging an edge.
  bitmap = padBitmapTo(bitmap, widthDots, "center");

  return {
    bitmap,
    layout,
    lengthMm: Math.round(bitmap.height / DOTS_PER_MM),
  };
}
